import React, { PureComponent } from 'react';
import { withTranslation, Trans } from 'react-i18next';

import { sendMessage, rejectMessage } from '../actions/messages';

import { highlightText, copyUrlToClipboard } from 'utils/index';
import { buildAppUrl } from 'utils/url';
import { BOT } from '../constants';

import UserContainer from '../containers/user-container';

import UserName from './user-name';
import NiftyMarkdown from './nifty-markdown';
import MessageUser from './message-user';
import MessageGif from './message-gif';
import MessageSeenBy from './message-seen-by';
import DateTime from 'components/DateTime';
import Tooltip from 'components/tooltip';

import IconTick from '../icons/icon-tick';
import IconPin from '../icons/icon-pin';
import IconEyeStrike from '../icons/icon-eye-strike';

const DISABLED_MD_RULES = ['heading'];

const MessageDateTime = ({ discussionId, messageId, projectId, canLink, createdAt, dateFormat }) => {
    const handleTimeClick = () => {
        if (projectId) {
            copyUrlToClipboard(buildAppUrl(`/${projectId}/discussion/message/${messageId}`));
        } else {
            copyUrlToClipboard(buildAppUrl(`/chat/${discussionId}/message/${messageId}`));
        }
    }

    return (
        <Tooltip withTooltipHover={false} tooltip={ <DateTime format="fullDateTime">{createdAt}</DateTime> } placement="top">
            <span
                onClick={canLink ? handleTimeClick : undefined}
                className={canLink ? 'meta-action' : ''} data-serialize-before=" [" data-serialize-after="]\n"
            >
                <DateTime format={dateFormat}>{createdAt}</DateTime>
            </span>
        </Tooltip>
    );
}

class Message extends PureComponent {
    static defaultProps = {
        dateFormat: 'time',
        className: 'message',
        attachments: []
    }

    state = {
        sending: false
    }

    handleUsernameClick = () => {
        if (!this.props.clicksEnabled) {
            return;
        }

        this.userProfileCtrl && this.userProfileCtrl.showPopout();
    }

    handleResendClick = () => {
        this.setState({
            sending: true
        });

        sendMessage(this.props.fragment)
            .then(() => {
                this.setState({
                    sending: false
                });
            }).catch(() => {
                this.setState({
                    sending: false
                });
            });

        rejectMessage(this.props.id);
    }

    handleRejectClick = () => {
        rejectMessage(this.props.id);
    }

    addHighlight(input) {
        if (!this.props.highlighted) {
            return input;
        }

        return highlightText(input, this.props.highlighted, true);
    }

    setElement = element => this.element = element;

    getMessageBody() {
        if (this.props.getMessageBody) {
            return this.props.getMessageBody(this.htmlContent);
        }

        return (
            <div className="message-body">
                <div className="message-body-inner">
                    <div className="message-body-main">
                        <div className="pane">
                            { this.htmlContent }
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    getControlsElement() {
        if (this.props.getControlsElement) {
            return this.props.getControlsElement();
        }

        return null
    }

    get htmlContent() {
        this.hasMention = false;

        const { t } = this.props;

        if (this.props.removed) {
            return <p>{ t('This message has been removed') }</p>;
        }

        if (this.props.type === 'gif') {
            return <MessageGif url={this.props.url} size={this.props.gifSize} />
        }

        if (!this.props.text) {
            return '';
        }

        return <NiftyMarkdown disabledRules={DISABLED_MD_RULES}>{this.props.text}</NiftyMarkdown>
    }

    get isSelf() {
        return this.props.author === this.props.currentUserId;
    }

    render() {
        const { t } = this.props;

        if (this.props.placeholder) {
            return (
                <div className={'message placeholder' + (this.props.placeholderAlt ? ' placeholder-alt' : '')}>
                    <div className="message-side">
                        <div className="message-user">
                            <span className="placeholder-particle" />
                        </div>
                    </div>

                    <div className="message-main">
                        <div className="message-head">
                            <h4 className="message-username">
                                <span className="placeholder-particle placeholder-particle-large" />
                            </h4>

                            <ul className="message-meta">
                                <li>
                                    <span className="placeholder-particle placeholder-particle-large" />
                                </li>
                            </ul>
                        </div>

                        <div className="message-body">
                            <span className="placeholder-particle placeholder-particle-medium" />

                            <span className="placeholder-particle placeholder-particle-medium" />

                            <span className="placeholder-particle placeholder-particle-medium" />
                        </div>
                    </div>
                </div>
            );
        }

        if (!this.props.attachments.length && !this.htmlContent && !this.props.docAttachment) {
            return null;
        }

        return (
            <div className={this.props.className} data-message-id={this.props.id} ref={this.setElement}>
                {
                    this.props.pinnedMessage === this.props.id && this.props.pinnedMessageBy ? (
                        <div className="message-pinned-by"><IconPin /> <Trans i18nKey="chat:Pinned by" components={{ UserName: <UserName id={this.props.pinnedMessageBy} /> }} /></div>
                    ) : null
                }

                <div className="message-inner">
                    <div className="message-side" data-serialize-ignore="true">
                        {
                            (this.props.system && !this.props.systemAsAction) || this.props.botName ? (
                                <MessageUser
                                    name={this.props.botName || BOT.name}
                                    avatar={this.props.botAvatar || BOT.image}
                                    bot={true}
                                    noTooltip={this.props.botName}
                                />
                            ) : (
                                <UserContainer id={this.props.author}>
                                    <MessageUser provideController={ctrl => this.userProfileCtrl = ctrl} />
                                </UserContainer>
                            )
                        }

                        <div className="message-meta">
                            <MessageDateTime
                                discussionId={this.props.discussionId}
                                messageId={this.props.id}
                                projectId={this.props.projectId}
                                canLink={this.props.canLinkMessage}
                                createdAt={this.props.createdAt}
                                dateFormat="shortTime"
                            />
                        </div>
                    </div>

                    <div className="message-main">
                        <div className="message-head">
                            <div className="message-head-main">
                                <h4 className="message-username" onClick={this.handleUsernameClick} data-serialize-ignore-styles="true">
                                    {
                                        ((this.props.system && !this.props.systemAsAction && (!this.props.botUseUserName || !this.props.author)) || this.props.botName) ? this.props.botName || BOT.name : (
                                            <UserName id={this.props.author} showPending={true} />
                                        )
                                    }
                                </h4>

                                <ul className="message-meta" data-serialize-ignore-styles="true">
                                    <li data-serialize-ignore-styles="true">
                                        <MessageDateTime
                                            discussionId={this.props.discussionId}
                                            messageId={this.props.id}
                                            projectId={this.props.projectId}
                                            canLink={this.props.canLinkMessage}
                                            createdAt={this.props.createdAt}
                                            dateFormat={this.props.dateFormat}
                                        />
                                    </li>

                                    {
                                        this.isSelf && !this.props.system ? (
                                            <li data-serialize-ignore="true">
                                                {
                                                    (this.props.seenBy && this.props.seenBy.some(userId => userId !== this.props.currentUserId)) ? <MessageSeenBy users={this.props.seenBy} currentUserId={this.props.currentUserId} /> : (!this.props.pending ? <IconTick size="11px" color="#ababab" /> : null)
                                                }
                                            </li>
                                        ) : null
                                    }

                                    {
                                        this.props.hidden?.length ? (
                                            <li data-serialize-ignore="true">
                                                <Tooltip tooltip={ t('Message is hidden from guests') }>
                                                    <span style={{ lineHeight: 0 }}>
                                                        <IconEyeStrike />
                                                    </span>
                                                </Tooltip>
                                            </li>
                                        ) : null
                                    }
                                </ul>
                            </div>

                            {
                                this.props.error && !this.state.sending ? (
                                    <div className="message-error-cta" data-serialize-ignore="true">
                                        { t('The message wasn’t sent') }

                                        {
                                            this.props.fragment ? (
                                                <span className="action" onClick={this.handleResendClick}>{ t('common:Try again') }</span>
                                            ) : null
                                        }
                                        •
                                        <span className="action" onClick={this.handleRejectClick}>{ t('common:Cancel') }</span>
                                    </div>
                                ) : null
                            }
                        </div>

                        { this.getMessageBody() }

                        { this.getControlsElement() }
                    </div>
                </div>
            </div>
        );
    }
}

export default withTranslation(['chat', 'common'])(Message);
